<template>
  <div>
    <!-- fixed is set to avoid issues where cells change size when sorting/filtering/pagination takes place -->
    <!-- if you want to implement a non-fixed table, make sure to account for those potential issues -->
    <!-- see: https://linear.app/userevidence/issue/UE-1351/use-fixed-layout-for-uetable -->
    <div :class="tableContainerClass">
      <b-table
        class="ue-table"
        :class="classObject"
        v-bind="$attrs"
        v-on="$listeners"
        :head-variant="variant"
        :per-page="perPage"
        :current-page="internalCurrentPage"
        :items="() => items"
        :fields="fields"
        :sort-by="sortBy"
        :sort-desc="sortDesc"
        empty-text="Nothing to show"
        sort-icon-left
        show-empty
        fixed
        no-local-sorting
        ref="ueTable"
        @sort-changed="onSortChanged"
      >
        <!-- Allow customization of column widths -->
        <!-- Approach taken from: https://bootstrap-vue.org/docs/components/table#table-colgroup -->
        <template #table-colgroup="scope">
          <col
            v-for="field in scope.fields"
            :key="field.key"
            :style="{ width: columnWidths[field.key] }"
            :data-cy="field.key + '-column'"
          />
        </template>

        <!-- b-table uses scoped named slots to allow for custom rendering of cells. -->
        <!-- This business here passes through all scoped slots from parent. -->
        <!-- So for instance you can do this in parent: <UeTable><template #cell(name)="data"></template></UeTable> -->
        <!-- see: https://bootstrap-vue.org/docs/components/table#scoped-field-slots -->
        <!--      https://stackoverflow.com/questions/53430731/vuejs-nested-slots-how-to-pass-slot-to-grandchild -->
        <template v-for="(_, slot) in $scopedSlots" v-slot:[slot]="props">
          <slot :name="slot" v-bind="props" />
        </template>
      </b-table>
    </div>
    <div class="mt-3" v-if="totalRows > 0 && !hidePaginationControls">
      <b-pagination
        v-model="internalCurrentPage"
        :total-rows="totalRows"
        :per-page="perPage"
        :limit="paginationLimit"
        :hide-goto-end-buttons="paginationHideGotoEndButtons"
        @change="onPageChanged"
      />
      <small>Displaying {{ startOfCurrentPage }}-{{ endOfCurrentPage }} of {{ totalRows }}</small>
    </div>
  </div>
</template>

<script lang="ts">
import Vue from 'vue'

export default Vue.extend({
  inheritAttrs: false,
  data() {
    return {
      allRowsExpanded: false,
    }
  },
  props: {
    // Supply custom widths for specific columns. An object where keys are column names
    // (as defined in the table `items` prop) and values are valid CSS widths.
    // Example: {name: '10%'}
    columnWidths: {
      type: Object,
      required: false,
      default: () => {
        return {}
      },
    },
    useDefaultStyle: {
      type: Boolean,
      required: false,
      default: false,
    },
    paginationLimit: {
      type: Number,
      required: false,
      default: 5, // Default BootstrapVue pagination limit
    },
    paginationHideGotoEndButtons: {
      type: Boolean,
      required: false,
      default: false,
    },
    hidePaginationControls: {
      type: Boolean,
      required: false,
      default: false,
    },
    cardRows: {
      type: Boolean,
      required: false,
      default: false,
    },
    hideOverflow: {
      type: Boolean,
      required: false,
      default: true,
    },
    totalRows: {
      type: Number,
      required: false,
      default: Number.MAX_SAFE_INTEGER,
    },
    currentPage: {
      type: [Number, String],
      required: false,
      default: 1,
    },
    perPage: {
      type: Number,
      required: false,
      default: 10,
    },
    items: {
      type: Array,
      required: true,
      default: () => {
        return [] as Array<unknown>
      },
    },
    fields: {
      type: Array,
      required: true,
      default: () => {
        return [] as Array<unknown>
      },
    },
    sortBy: {
      type: String,
      required: false,
      default: '',
    },
    sortDesc: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  computed: {
    // Computed property with getter and setter to handle currentPage prop
    internalCurrentPage: {
      get(): number {
        return Number(this.currentPage)
      },
      set(value: number) {
        this.$emit('update:currentPage', value)
      },
    },
    // There are cases where we want the default Bootstrap table,
    // without the customizations of the Results UI.
    // Ideally, the default should the plain Bootstrap table, and the customizations
    // should be opt-in. However, we have quite a few tables that are already using the default,
    // so we're doing the other way around for now.
    // TODO: Discuss this and maybe change the current usages.
    classObject() {
      return {
        'ue-table': !this.useDefaultStyle,
        'ue-table-empty': this.items.length === 0,
        // Standard rows gives you typical table rows
        'standard-rows': !this.cardRows,
        // Card rows gives you card-style rows with gaps
        'card-rows': this.cardRows,
      }
    },
    variant() {
      if (this.cardRows) {
        return 'secondary'
      } else {
        return this.useDefaultStyle ? '' : 'dark'
      }
    },
    tableContainerClass() {
      return {
        rounded: !this.cardRows,
        border: !this.cardRows,
        'overflow-hidden': this.hideOverflow,
      }
    },
    startOfCurrentPage() {
      return (this.internalCurrentPage - 1) * this.perPage + 1
    },
    endOfCurrentPage() {
      return Math.min(this.internalCurrentPage * this.perPage, this.totalRows)
    },
  },
  methods: {
    toggleAllDetails() {
      const items = this.$refs.ueTable?.localItems || []
      items.forEach((item: unknown) => {
        this.$set(item, '_showDetails', !this.allRowsExpanded)
      })
      this.allRowsExpanded = !this.allRowsExpanded
    },
    onPageChanged(page: number) {
      this.$emit('page-changed', page)
    },
    onSortChanged({ sortBy, sortDesc }: { sortBy: string; sortDesc: boolean }) {
      this.$emit('sort-changed', { sortBy, sortDesc })
    },
  },
})
</script>

<style lang="sass" scoped>
::v-deep
  .table.card-rows
    background-color: #f8f9fa !important
    border-collapse: separate
    border-spacing: 0 1rem
    border: none

    thead
      th
        border-top: none
        border-bottom: 1px solid #CED4DA !important
        font-size: var(--font-size-xsm)
    tbody
      tr
        background-color: white
      th
        color: white
        text-align: center
      th, td
        width: 150px
        padding: 5px
      td
        vertical-align: top
        border-top: solid 1px var(--ueSecondaryGray) !important
        border-bottom: solid 1px var(--ueSecondaryGray) !important
        padding-top: 2rem
        padding-bottom: 2rem
      td:first-child
        border-left: solid 1px var(--ueSecondaryGray) !important
        border-top-left-radius: var(--border-radius)
        border-bottom-left-radius: var(--border-radius)
      td:last-child
        border-right: solid 1px var(--ueSecondaryGray) !important
        border-top-right-radius: var(--border-radius)
        border-bottom-right-radius: var(--border-radius)

  .table
    margin-bottom: 0 !important // this important is needed to override the default b-table margin-bottom of 1rem

  table.ue-table-empty
    thead
      display: none !important
    td
      border: none !important

  table.standard-rows
    thead
      th
        border-bottom: none!important // overriding a Bootstrap !important
    tbody
      td
        font-size: var(--font-size-sm)

  table.ue-table
    tbody
      tr:hover:not(.b-table-details)
        cursor: pointer
      tr.b-table-details
        td
          padding: 0 !important
      tr.b-table-details:hover
        background-color: transparent // if you want a hover effect, do it on the slot content

  table.b-table[aria-busy='true']
    // Overrides the 0.55 for when table is busy
    // as this usually just causes it to flicker.
    opacity: 1

  thead
    th
      border-top: none // Fixes a Firefox glitch where the border appears despite border-top-left-radius on the parent table
      border-bottom: none!important // overriding a Bootstrap !important
      white-space: nowrap
</style>
